import React, { useState } from "react";
import PageHero from "@/components/shared/PageHero";
import { FormShell, SuccessPopup } from "@/components/shared/FormShell";
import { DISTRICTS } from "@/data/mock";
import { Upload, Clock, ShieldCheck, MailCheck } from "lucide-react";

export default function RequestMeeting() {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: "", mobile: "", email: "", district: "", constituency: "",
        purpose: "", date: "", description: "",
    });
    const onSubmit = (e) => {
        e.preventDefault();
        setOpen(true);
    };
    const code = "MWK-2026-" + Math.floor(100000 + Math.random() * 900000);

    return (
        <>
            <PageHero
                testId="request-meeting-page"
                eyebrow="Request a Meeting"
                chapter="Service · 01"
                title="Request a meeting."
                subtitle="Share who you are, why you'd like to meet, and when. You'll receive a request ID to track the meeting end-to-end."
            />
            <FormShell
                aside={
                    <div>
                        <div className="eyebrow mb-4">What happens next</div>
                        <ul className="space-y-6">
                            {[
                                { Icon: Clock, t: "Received in 2 hours", s: "You'll get a request ID immediately." },
                                { Icon: ShieldCheck, t: "Reviewed & routed", s: "Assigned to the right district cell." },
                                { Icon: MailCheck, t: "Confirmation", s: "Scheduling updates arrive by email & SMS." },
                            ].map(({ Icon, t, s }, i) => (
                                <li key={i} className="flex items-start gap-4">
                                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-ink/10 bg-white">
                                        <Icon className="h-4 w-4 text-pink" />
                                    </div>
                                    <div>
                                        <div className="font-display text-[15px] font-semibold text-ink">{t}</div>
                                        <div className="text-[13px] text-slate-500">{s}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8 rounded-2xl border border-ink/10 bg-white p-4 text-[12px] leading-relaxed text-slate-500">
                            <strong className="font-semibold text-ink">Note:</strong> This is a concept prototype — no
                            data is stored, transmitted or acted upon.
                        </div>
                    </div>
                }
            >
                <form className="space-y-8" onSubmit={onSubmit} data-testid="request-meeting-form">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="field">
                            <label>Full name</label>
                            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" data-testid="rm-name" />
                        </div>
                        <div className="field">
                            <label>Mobile number</label>
                            <input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91 00000 00000" data-testid="rm-mobile" />
                        </div>
                        <div className="field">
                            <label>Email</label>
                            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@domain.com" data-testid="rm-email" />
                        </div>
                        <div className="field">
                            <label>District</label>
                            <select required value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="rm-district">
                                <option value="">Select district</option>
                                {DISTRICTS.map((d) => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                        <div className="field">
                            <label>Constituency</label>
                            <input required value={form.constituency} onChange={(e) => setForm({ ...form, constituency: e.target.value })} placeholder="Your constituency" data-testid="rm-constituency" />
                        </div>
                        <div className="field">
                            <label>Preferred date</label>
                            <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="rm-date" />
                        </div>
                    </div>
                    <div className="field">
                        <label>Purpose</label>
                        <input required value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="A one-line summary" data-testid="rm-purpose" />
                    </div>
                    <div className="field">
                        <label>Description</label>
                        <textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Give context — background, what you'd like to discuss, and expected outcome." data-testid="rm-description" />
                    </div>
                    <div className="field">
                        <label>Upload supporting documents</label>
                        <label className="mt-2 flex cursor-pointer items-center justify-between rounded-2xl border border-dashed border-ink/20 bg-slate-50 px-6 py-6 text-[13px] text-slate-500 hover:border-pink hover:text-pink transition-colors">
                            <span className="flex items-center gap-3">
                                <Upload className="h-4 w-4" />
                                Attach PDFs, images or documents (UI only)
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.24em]">Optional</span>
                            <input type="file" className="hidden" data-testid="rm-upload" />
                        </label>
                    </div>
                    <div className="flex items-center gap-4 pt-4">
                        <button type="submit" className="btn-primary" data-testid="rm-submit">Submit request</button>
                        <span className="text-[12px] text-slate-500">By submitting, you accept the concept prototype terms.</span>
                    </div>
                </form>
            </FormShell>
            <SuccessPopup
                open={open}
                onClose={() => setOpen(false)}
                title="Meeting request received"
                body="Thanks for reaching out. In a real deployment, you would receive confirmation on your email and mobile within 2 hours."
                code={code}
            />
        </>
    );
}
